// frontend/src/components/layout/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import API from '../../services/api';
import { useApi } from '../../hooks/useApi';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { data: settings } = useApi(() => API.settings.get());

  return (
    <footer className="w-full bg-gradient-to-b from-gray-900 to-black text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute top-20 right-20 w-40 h-40 bg-emerald-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-32 h-32 bg-gold rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-8 md:px-12 lg:px-16 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-start">
          {/* Logo + Blurb */}
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center group" onClick={() => setIsMobileMenuOpen(false)}>
                <img src="/logo-new.png" alt="Layou Adventures" className="h-32 w-32 object-contain group-hover:scale-110 transition-transform duration-300" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                <span className="font-playfair text-3xl font-bold ml-2 text-white group-hover:text-gold transition-colors duration-300">LayouAdventures</span>
              </Link>
            </div>
            <p className="font-graphik text-gray-300 max-w-md text-lg leading-relaxed">
              Authentic island adventures in St. Vincent , expert guides, safe equipment, and unforgettable memories. Book your next trip with us and discover paradise.
            </p>

            {/* Social Media Icons */}
            <div className="flex items-center space-x-4">
              <a href="https://www.instagram.com/layouadventures/" target="_blank" rel="noopener noreferrer" className="group w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gradient-to-br hover:from-pink-500 hover:to-purple-600 transition-all duration-300">
                <svg className="w-6 h-6 text-gray-300 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              <a href="https://www.youtube.com/watch?v=SLc7qmWD4G8" target="_blank" rel="noopener noreferrer" className="group w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors duration-300">
                <svg className="w-6 h-6 text-gray-300 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
              </a>
              <a href="https://www.facebook.com/p/Layou-Adventures-61568438644995/" target="_blank" rel="noopener noreferrer" className="group w-12 h-12 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors duration-300">
                <svg className="w-6 h-6 text-gray-300 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="font-playfair text-xl font-bold mb-6 text-white border-b border-emerald-600 pb-2">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="font-graphik text-gray-300 hover:text-gold transition-colors duration-300 flex items-center group">
                <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3 group-hover:bg-gold transition-colors duration-300"></span>
                Home
              </Link></li>
              <li><Link to="/tours" className="font-graphik text-gray-300 hover:text-gold transition-colors duration-300 flex items-center group">
                <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3 group-hover:bg-gold transition-colors duration-300"></span>
                Tours
              </Link></li>
              <li><Link to="/blog" className="font-graphik text-gray-300 hover:text-gold transition-colors duration-300 flex items-center group">
                <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3 group-hover:bg-gold transition-colors duration-300"></span>
                Blog
              </Link></li>
              <li><Link to="/contact" className="font-graphik text-gray-300 hover:text-gold transition-colors duration-300 flex items-center group">
                <span className="w-2 h-2 bg-emerald-600 rounded-full mr-3 group-hover:bg-gold transition-colors duration-300"></span>
                Contact
              </Link></li>
            </ul>
          </div>

          {/* Contact & Newsletter */}
          <div className="space-y-6">
            <h4 className="font-playfair text-xl font-bold mb-6 text-white border-b border-emerald-600 pb-2">Stay Connected</h4>

            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-gray-300">
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span className="font-graphik"> {settings?.contact_phone || '(614) 385-2846'}</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-graphik"> {settings?.contact_email || 'info@layouadventures.com'}</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-300">
                <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="font-graphik"> {settings?.contact_address || 'Layou, St. Vincent'}</span>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="w-full bg-black border-t border-gray-800 text-gray-400 text-center py-6">
        <div className="container mx-auto px-8 md:px-12 lg:px-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-sm font-graphik">© {currentYear} LayouAdventures. All rights reserved.</div>
            <div className="flex space-x-6 text-sm font-graphik">
              <a href="#" className="hover:text-white transition-colors duration-300">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors duration-300">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors duration-300">Sitemap</a>

              {/* <li><Link to="/privacy" className="hover:text-white transition-colors duration-300">
                Privacy Policy
              </Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors duration-300">
                Terms of Service
              </Link></li>
              <li><Link to="/sitemap" className="hover:text-white transition-colors duration-300">
                Sitemap
              </Link></li> */}

            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;